/**
 * The lowest price each currency has ever sold for in a public buy-points sale, in US cents
 * per point, so a sale can be judged against the best there has been. Priced the way the
 * briefs quote sales: US airlines include the 7.5% federal excise tax; everything else is the
 * price before any tax, converted to dollars at the time for programs that sell in other
 * currencies. Checked in October 2026; a newer, cheaper sale in my own briefs beats these on
 * its own. Accor, KrisFlyer and Asia Miles aren't here: they don't sell points outright.
 */

export interface BuyRecord {
  cpp: number;
  /** YYYY-MM, or YYYY when only the year is known. */
  when: string | null;
  /** Like "120% bonus". */
  promo: string | null;
  source: string;
}

/** By programId (see valuations.ts), or another seller's key (see saleKey in news.ts). */
export const BUY_RECORDS: Record<string, BuyRecord> = {
  // Hotels
  marriott: {
    cpp: 0.78,
    when: '2020-05',
    promo: '60% bonus',
    source: 'https://frequentmiler.com/60-bonus-on-marriott-points-5-18-to-6-30-buy-for-0-78cpp/',
  },
  hyatt: {
    cpp: 1.68,
    when: '2022-08',
    promo: '30% off',
    source:
      'https://loyaltylobby.com/2022/08/22/world-of-hyatt-buy-points-promotion-30-discount-through-september-30-2022/',
  },
  hilton: {
    cpp: 0.45,
    when: '2026-10',
    promo: '120% bonus',
    source: 'https://thepointsguy.com/deals/hilton-best-ever-buy-points-promotion/',
  },
  // 0.48¢ only when buying exactly 35,000; the usual floor is 0.5¢ (100% bonus).
  ihg: {
    cpp: 0.48,
    when: '2026-10',
    promo: '100% bonus + 3,500 on 35k',
    source: 'https://frequentmiler.com/buy-ihg-points/',
  },
  wyndham: {
    cpp: 0.65,
    when: '2026-03',
    promo: '100% bonus, targeted',
    source: 'https://onemileatatime.com/deals/buy-wyndham-rewards-points/',
  },
  choice: {
    cpp: 0.52,
    when: '2025-07',
    promo: '50% off with a Choice card',
    source:
      'https://frequentmiler.com/choice-privileges-points-sale-get-up-to-50-off-buy-points-for-0-52cpp/',
  },
  // US airlines, tax included
  delta: {
    cpp: 1.5,
    when: '2010-05',
    promo: '100% bonus, Delta Amex holders',
    source:
      'https://viewfromthewing.com/new-delta-american-express-promo-and-benefit-100-bonus-on-mileage-purchases-and-first-checked-bag-free/',
  },
  american: {
    cpp: 1.72,
    when: '2018-07',
    promo: '10% off + 100k bonus on 150k',
    source:
      'https://viewfromthewing.com/lowest-ever-price-to-buy-american-miles-now-through-august-1',
  },
  // The usual best is a 100% bonus at 1.88¢; in 2020 the tax was waived, so it came to 1.75¢.
  united: {
    cpp: 1.75,
    when: '2020',
    promo: '100% bonus, tax waived',
    source: 'https://onemileatatime.com/united-buy-miles-promo',
  },
  southwest: {
    cpp: 1.5,
    when: '2026-10',
    promo: '50% off, targeted',
    source: 'https://onemileatatime.com/deals/buy-southwest-rapid-rewards-points',
  },
  alaska: {
    cpp: 1.71,
    when: '2026-10',
    promo: '120% bonus, targeted',
    source: 'https://upgradedpoints.com/news/buy-alaska-atmos-rewards-points/',
  },
  jetblue: {
    cpp: 1.32,
    when: '2024-02',
    promo: '90% bonus, targeted',
    source:
      'https://loyaltylobby.com/2024/02/04/jetblue-buy-points-up-to-90-bonus-through-march-31-2024/',
  },
  // Other airlines
  aeroplan: {
    cpp: 1.13,
    when: '2023-11',
    promo: '125% bonus, targeted',
    source:
      'https://viewfromthewing.com/up-to-125-bonus-buying-air-canada-miles-1-13-cents-apiece-amazing',
  },
  flyingblue: {
    cpp: 1.39,
    when: '2022-06',
    promo: '120% bonus',
    source: 'https://thepointsguy.com/news/flying-blue-buy-miles-promo/',
  },
  // Avios move freely between BA, Iberia and Qatar, so the cheapest of the three counts.
  avios: {
    cpp: 1.17,
    when: '2022-09',
    promo: 'Iberia, 50% bonus',
    source: 'https://awardwallet.com/airlines/buy-iberia-avios/',
  },
  virgin: {
    cpp: 1.48,
    when: '2026-09',
    promo: '70% bonus',
    source: 'https://onemileatatime.com/deals/buy-virgin-atlantic-flying-club-points/',
  },
  emirates: {
    cpp: 1.5,
    when: '2021-05',
    promo: '100% bonus, flash sale',
    source:
      'https://loyaltylobby.com/2021/05/06/emirates-skywards-buy-miles-up-to-100-bonus-flash-sale-until-may-7-2021/',
  },
  turkish: {
    cpp: 1.5,
    when: '2026-10',
    promo: '100% bonus',
    source: 'https://upgradedpoints.com/news/buy-turkish-airlines-miles',
  },
  lifemiles: {
    cpp: 1.1,
    when: '2024-09',
    promo: '200% bonus, targeted',
    source:
      'https://frequentmiler.com/targeted-lifemiles-giving-200-bonus-on-purchased-points-buy-for-1-1cpp-best-sale-ever/',
  },
  starlux: {
    cpp: 2.4,
    when: '2026-10',
    promo: '40% off, its first sale',
    source: 'https://onemileatatime.com/news/buy-starlux-airlines-miles/',
  },
  etihad: {
    cpp: 1.18,
    when: '2021-11',
    promo: '70% bonus, flash sale',
    source:
      'https://loyaltylobby.com/2021/11/22/amazing-etihad-guest-flash-sale-to-buy-miles-70-bonus-through-22-november-2021/',
  },
};
